import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUpRight, BarChart3, ClipboardCheck, Percent, Target } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { HeroCard } from "@/components/blocks/HeroCard";
import { ROUTE_WAITLIST } from "@/lib/brand";
import { COMMISSION_PERCENT } from "@/lib/pricing";

const AT_A_GLANCE = [
  {
    icon: Target,
    title: "Zueinander finden",
    text: "Vereine und Unternehmen finden über klare Kriterien und den MatchScore zusammen.",
  },
  {
    icon: ClipboardCheck,
    title: "Sponsoring einrichten",
    text: "Beim Abschluss halten beide Seiten fest, was vereinbart ist.",
  },
  {
    icon: BarChart3,
    title: "Wirkung belegen",
    text: "Jede Leistung belegt, jede Zahl mit Herkunft, über die ganze Saison.",
  },
  {
    icon: Percent,
    title: "Fair bezahlt",
    text: `Ein Abo je Seite, ${COMMISSION_PERCENT} % Provision nur bei Vertragsschluss.`,
  },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Text und Karte gleiten beim Scrollen gemeinsam weg, aber nur ab lg. Darunter
  // stehen sie untereinander, dort bleibt der Kopfbereich ruhig.
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setWide(mq.matches);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[calc(100vh-var(--bar-h))] flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Glow / portal backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 -left-20 w-[520px] h-[520px] rounded-full bg-brand-orange/25 blur-[140px] animate-pulseGlow" />
        <div className="absolute bottom-0 right-0 w-[560px] h-[560px] rounded-full bg-brand-blue/25 blur-[150px] animate-pulseGlow" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 30%, transparent 75%)",
          }}
        />
      </div>

      <motion.div
        style={wide ? { y, opacity } : undefined}
        className="w-full px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-center"
      >
        {/* Left - kinetic type */}
        <div className="lg:col-span-7 [container-type:inline-size]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-brand-orange shadow-[0_0_10px_2px_rgba(255,107,0,0.8)]" />
            <span className="text-xs md:text-sm tracking-[0.15em] uppercase font-bold text-white/70">
              Belegt statt behauptet
            </span>
          </motion.div>

          {/* Die Zeilen sind fest gesetzt. Die Schrift richtet sich nach der
              Spaltenbreite (cqi), gedeckelt je Breakpoint, damit keine Zeile
              umbricht und Wörter einzeln stehen. 10.8cqi passt zur längsten
              Zeile „sondern belegen lässt.“ mit etwas Reserve. */}
          <h1 className="font-display font-black tracking-tighter text-[length:min(10.8cqi,3rem)] md:text-[length:min(10.8cqi,3.75rem)] lg:text-[length:min(10.8cqi,4.5rem)] leading-[0.95]">
            <MaskedLines
              lines={["Sponsoring, das sich", "nicht nur gut anfühlt,"]}
              delay={0.25}
            />
            <span className="line-mask block">
              <motion.span
                className="block gradient-text"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 1,
                  ease: [0.16, 1, 0.3, 1],
                  delay: 0.49,
                }}
              >
                sondern belegen lässt.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-white/60"
          >
            Sportvereine finden Partner, die wirklich zu ihnen passen.
            Unternehmen sehen schwarz auf weiß, was ihr Engagement bewirkt.
            Beides auf einer Plattform - vom ersten Kontakt bis zur Auswertung.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link
              to={ROUTE_WAITLIST}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_35px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_45px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.04] transition-all duration-300"
            >
              Platz vormerken
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <a
              href="#plattform"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center gap-2 rounded-full px-6 py-4 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300"
            >
              So funktioniert's
              <ArrowDown className="w-4 h-4 text-brand-blue group-hover:translate-y-0.5 transition-transform duration-300" />
            </a>
          </motion.div>

          {/* AUSGEBLENDET - Kennzahlen-Zeile. Wieder einblenden, sobald echte
              Zahlen vorliegen: einfach die umschließenden Kommentarzeichen
              entfernen und Platzhalter durch die Werte ersetzen.

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-14 flex items-center gap-8"
          >
            {[
              ["n/A", "Vereine"],
              ["n/A", "Unternehmen"],
              ["n/A", "vermittelt"],
            ].map(([num, label]) => (
              <div key={label}>
                <div className="font-display font-bold text-2xl md:text-3xl text-white">
                  {num}
                </div>
                <div className="text-xs uppercase tracking-widest text-white/40 mt-1">
                  {label}
                </div>
              </div>
            ))}
          </motion.div>

          */}
        </div>

        {/* Rechts: MatchGate auf einen Blick */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <HeroCard kicker="MatchGate auf einen Blick" items={AT_A_GLANCE} />
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll hint. Bis lg steht die Karte darunter, dort wäre er
          nicht am Bildschirmrand und läge auf der Karte. */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
          Scroll
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
};
